import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrencyEUR, formatDate, formatRelative } from "@/lib/utils";

type HubspotType = "string" | "enumeration" | "date" | "datetime" | "number" | "bool";

type MapEntry = {
  hubspot_property_name: string;
  display_label: string;
  display_section: string;
  hubspot_type: HubspotType;
  enum_options: Array<{ label: string; value: string }> | null;
  sort_order: number;
};

const DEFAULT_SECTION = "HubSpot details";

function compareSections(a: string, b: string): number {
  // 'HubSpot details' (the default) sorts last so explicit sections come first.
  if (a === DEFAULT_SECTION && b !== DEFAULT_SECTION) return 1;
  if (b === DEFAULT_SECTION && a !== DEFAULT_SECTION) return -1;
  return a.localeCompare(b);
}

function renderValue(
  entry: MapEntry,
  raw: string | null,
): React.ReactNode {
  if (raw == null || raw === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  switch (entry.hubspot_type) {
    case "enumeration": {
      const match = entry.enum_options?.find((o) => o.value === raw);
      return <span>{match?.label ?? raw}</span>;
    }
    case "date":
      return <span>{formatDate(raw)}</span>;
    case "datetime":
      return (
        <span title={new Date(raw).toLocaleString()}>{formatRelative(raw)}</span>
      );
    case "number": {
      const num = Number(raw);
      if (Number.isNaN(num)) return <span>{raw}</span>;
      const name = entry.hubspot_property_name.toLowerCase();
      const isCurrency =
        name.includes("eur") ||
        name.includes("revenue") ||
        name.includes("arr") ||
        name.includes("amount");
      return (
        <span className="tabular-nums">
          {isCurrency ? formatCurrencyEUR(num) : num.toLocaleString()}
        </span>
      );
    }
    case "bool": {
      const truthy = raw === "true" || raw === "1";
      return (
        <Badge variant={truthy ? "default" : "secondary"} className="text-[10px]">
          {truthy ? "Yes" : "No"}
        </Badge>
      );
    }
    case "string":
    default:
      return raw.length > 120 ? <TruncatedText text={raw} /> : <span>{raw}</span>;
  }
}

function TruncatedText({ text }: { text: string }) {
  return (
    <details className="group">
      <summary className="cursor-pointer list-none">
        <span>{text.slice(0, 120)}…</span>
        <span className="ml-2 text-xs text-primary group-open:hidden">Show more</span>
      </summary>
      <p className="mt-1 whitespace-pre-wrap">{text}</p>
    </details>
  );
}

export function PartnerHubspotExtrasPanels({
  extras,
  map,
}: {
  extras: Record<string, string | null>;
  map: MapEntry[];
}) {
  if (map.length === 0) return null;

  const neverSynced = Object.keys(extras).length === 0;

  const bySection = new Map<string, MapEntry[]>();
  for (const entry of map) {
    const section = entry.display_section || DEFAULT_SECTION;
    if (!bySection.has(section)) bySection.set(section, []);
    bySection.get(section)!.push(entry);
  }
  for (const [, entries] of bySection) {
    entries.sort((a, b) => a.sort_order - b.sort_order);
  }
  const sections = Array.from(bySection.entries()).sort(([a], [b]) =>
    compareSections(a, b),
  );

  return (
    <div className="space-y-4">
      {sections.map(([section, entries]) => (
        <Card key={section}>
          <CardHeader>
            <CardTitle className="font-display tracking-tight text-base">
              {section}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 sm:grid-cols-2">
              {entries.map((entry) => (
                <div key={entry.hubspot_property_name}>
                  <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    {entry.display_label}
                  </dt>
                  <dd className="mt-0.5 text-sm text-foreground">
                    {renderValue(entry, extras[entry.hubspot_property_name] ?? null)}
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      ))}
      {neverSynced && (
        <p className="text-[11px] text-muted-foreground">
          Not yet synced — run "Re-pull partners" in HubSpot settings or
          wait for the next propertyChange webhook.
        </p>
      )}
    </div>
  );
}
